import isMobileDevice from 'ismobilejs';
import { Dispatch, SetStateAction } from 'react';

export enum DEFAULT_SHORT_NAME {
  MODEL_3_5_4k = '3.5-4k',
  MODEL_3_5_16k = '3.5-16k',
  MODEL_4_TURBO = '4-turbo',
  MODEL_4_0_8k = '4-8k',
  MODEL_4o = '4o',
  MODEL_4o_8k = '4o-8k',
  MODEL_4o_16k = '4o-16k',
  MODEL_4o_mini = '4o-mini',
  MODEL_41 = '4.1-32k',
  MODEL_41_mini = '4.1-mini-32k',
  MODEL_41_nano = '4.1-nano',
  MODEL_4o_mini_8k = '4o-mini-8k',
  MODEL_4o_mini_16k = '4o-mini-16k',
  MODEL_54_mini = '5.4-mini',
  MODEL_54_nano = '5.4-nano',
  MODEL_56_luna = '5.6-luna',
}

// Infomaniak Swiss-hosted models (sovereign AI).
// Short names must match the Infomaniak entries in the backend's
// `apps/api/src/core/config/models/model/model-provider-configs.ts`.
export enum INFOMANIAK_MODEL {
  APERTUS_V1_5_70B = 'imk-apertus-1.5',
  MISTRAL_SMALL_4 = 'imk-mistral-small-4',
  QWEN3_5_122B = 'imk-qwen3.5-122b',
}

// All available models for the selector
export const AVAILABLE_MODELS: { id: string; name: string; group: 'infomaniak' | 'openai' }[] = [
  // Infomaniak Swiss-hosted models (sovereign AI)
  { id: INFOMANIAK_MODEL.APERTUS_V1_5_70B, name: 'Apertus 1.5 70B (Swiss Hosted at Infomaniak)', group: 'infomaniak' },
  {
    id: INFOMANIAK_MODEL.MISTRAL_SMALL_4,
    name: 'Mistral Small 4 119B (Swiss Hosted at Infomaniak)',
    group: 'infomaniak',
  },
  { id: INFOMANIAK_MODEL.QWEN3_5_122B, name: 'Qwen3.5 122B (Swiss Hosted at Infomaniak)', group: 'infomaniak' },
  // OpenAI models
  { id: DEFAULT_SHORT_NAME.MODEL_41_mini, name: 'OpenAI GPT-4.1-mini', group: 'openai' },
  { id: DEFAULT_SHORT_NAME.MODEL_56_luna, name: 'OpenAI GPT-5.6-luna', group: 'openai' },
  { id: DEFAULT_SHORT_NAME.MODEL_54_mini, name: 'OpenAI GPT-5.4-mini', group: 'openai' },
  { id: DEFAULT_SHORT_NAME.MODEL_54_nano, name: 'OpenAI GPT-5.4-nano', group: 'openai' },
];

export const DEFAULT_MODEL = DEFAULT_SHORT_NAME.MODEL_41_mini;

export interface UsedLinks {
  url: string;
  title?: string;
  type?: string;
  image?: string;
  smallImage?: string;
  date?: string;
  description?: string;
  orgMetadata?: Record<string, unknown>;
  considered: boolean;
  summary?: string;
}
//export const isMobile = true
export const isMobile = window ? isMobileDevice(window?.navigator).any : false;

const linkImageGetHrefForImage = (node: HTMLElement) => {
  const a = node.parentNode?.querySelector('a');
  return a?.getAttribute('href');
};

export const linkImageonMouserOver = (e: React.MouseEvent<HTMLDivElement>) => {
  const node = e.target as HTMLElement;

  if (node.nodeName === 'IMG') {
    const src = node.getAttribute('src');
    if (src && src.toLowerCase().includes('width-840')) {
      const parentWidth = node.parentElement?.offsetWidth || 840;
      const imgWidth = node.offsetWidth + 1;
      const maxScale = parentWidth / imgWidth;
      const scale = Math.min(7, maxScale); // Use 3 or maxScale, whichever is smaller

      node.style.transform = `scale(${scale})`;
    } else {
      node.style.transform = 'scale(3)';
    }
  }
};

export const linkImageOnMouseOut = (e: React.MouseEvent<HTMLDivElement>) => {
  const node = e.target as HTMLElement;
  if (node.nodeName === 'IMG') {
    node.style.transform = 'scale(1)';
  }
};

export const linkImageOnMouseClick = (e: React.MouseEvent<HTMLDivElement>) => {
  const node = e.target as HTMLElement;
  if (node.nodeName === 'IMG') {
    const href = linkImageGetHrefForImage(node);
    if (href) {
      window.open(href, '_blank');
    }
  }
};

export const replaceLinksWithCallback = (
  input: string,
  usedLinks: UsedLinks[],
  setResult: Dispatch<SetStateAction<string>>,
) => {
  const linkMatches = input.match(/\[\^([0-9]+)\^*]/g);
  if (linkMatches) {
    setResult(replaceLinks(input, usedLinks));
  }
};

export const replaceLinks = (input: string, usedLinks: UsedLinks[]) => {
  const linkMatches = input.match(/\[\^([\^0-9, ]+)]/g);

  if (linkMatches) {
    let newInput = input;

    linkMatches.forEach(fMatch => {
      fMatch.split(',').forEach(match => {
        const linkNumber = match.match(/([0-9]+)/g)?.[0];

        if (!linkNumber) {
          return;
        }
        const link = linkNumber && usedLinks[parseInt(linkNumber) - 1];
        if (link) {
          newInput = newInput.replace(match, ` ([Link ${linkNumber}](${link.url} "__link: ${linkNumber}"))`);
        } else {
          // if we don't have a link, remove the link
          newInput = newInput.replace(match, ``);
        }
      });
    });
    return newInput;
  }
  return input;
};

export const showGeneratedSummary = (result: {
  description?: string;
  summary?: string;
  type?: string | null | undefined;
}) => {
  if (!!result.summary && result.type === 'pdf') {
    return true;
  }
  return (!result.description || result.description.length < 140) && !!result.summary;
};

export function formatWithThousand(value: number): string {
  return Math.round(value).toLocaleString('de-CH').replace(/\./g, "'");
}
